import { $, JSXOutput, QRL, Signal, useContext } from "@builder.io/qwik";
import { FormProps, useNavigate } from "@builder.io/qwik-city";
import {
  FieldArrayPath,
  FieldArrayProps,
  FieldPath,
  FieldPathValue,
  FieldProps,
  FieldValues,
  FormStore,
  getValues,
  InitialValues,
  MaybeValue,
  PartialKey,
  ResponseData,
  useForm,
  ValidateForm,
} from "@modular-forms/qwik";
import {
  UserPreferencesContext,
  UserPreferencesFormContext,
} from "~/contexts/user-preferences";

export function useDiscoveryForm<
  TFieldValues extends FieldValues,
  TResponseData extends ResponseData = undefined,
>(
  validate: QRL<ValidateForm<TFieldValues>>,
  step: number,
): [
  FormStore<TFieldValues, TResponseData>,
  {
    Form: (
      props: Omit<FormProps<TFieldValues, TResponseData>, "of" | "action">,
    ) => JSXOutput;
    Field: <TFieldName extends FieldPath<TFieldValues>>(
      props: FieldPathValue<TFieldValues, TFieldName> extends MaybeValue<string>
        ? PartialKey<
            Omit<FieldProps<TFieldValues, TResponseData, TFieldName>, "of">,
            "type"
          >
        : Omit<FieldProps<TFieldValues, TResponseData, TFieldName>, "of">,
    ) => JSXOutput;
    FieldArray: <TFieldArrayName extends FieldArrayPath<TFieldValues>>(
      props: Omit<
        FieldArrayProps<TFieldValues, TResponseData, TFieldArrayName>,
        "of"
      >,
    ) => JSXOutput;
    handleNextStep: QRL<() => Promise<void>>;
    handlePreviousStep: QRL<() => Promise<void>>;
  },
];

export function useDiscoveryForm(
  validate: QRL<ValidateForm<FieldValues>>,
  step: number,
): [
  FormStore<FieldValues, ResponseData>,
  {
    Form: (
      props: Omit<FormProps<FieldValues, ResponseData>, "of" | "action">,
    ) => JSXOutput;
    Field: (
      props: Omit<FieldProps<FieldValues, ResponseData, string>, "of">,
    ) => JSXOutput;
    FieldArray: (
      props: Omit<FieldArrayProps<FieldValues, ResponseData, string>, "of">,
    ) => JSXOutput;
    handleNextStep: QRL<() => Promise<void>>;
    handlePreviousStep: QRL<() => Promise<void>>;
  },
] {
  const nav = useNavigate();
  const data = useContext(UserPreferencesContext);
  const formState = useContext(UserPreferencesFormContext);

  const [form, { Form, Field, FieldArray }] = useForm({
    loader: data as Readonly<Signal<InitialValues<FieldValues>>>,
    validate,
    validateOn: "input",
    revalidateOn: "input",
  });

  const handleNextStep = $(async () => {
    const values = getValues(form);

    data.value = {
      ...data.value,
      ...values,
    };
    formState.steps[formState.currentStepIndex] = true;

    const nextStep = +step >= 10 ? "processing" : step + 1;

    await nav("/discovery/steps/" + nextStep);
  });

  const handlePreviousStep = $(async () => {
    await nav("/discovery/steps/" + Math.max(1, Number(step - 1)));
  });

  return [
    form,
    {
      Form: Form as any,
      Field,
      FieldArray,
      handleNextStep,
      handlePreviousStep,
    },
  ];
}
